import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { money, StatusPill } from "./common";

const STATUSES = ["Order Received", "Payment Confirmed", "Being Prepared", "Ready for Dispatch", "Out for Delivery", "Delivered", "Ready for Collection", "Collected", "Delivery Issue", "Cancelled"];

export default function OrdersAdmin() {
    const [orders, setOrders] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [q, setQ] = useState("");
    const [detail, setDetail] = useState(null);
    const [statusForm, setStatusForm] = useState({ status: "", courier_name: "", courier_tracking_url: "", courier_notes: "" });
    const [refundAmount, setRefundAmount] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () =>
        api.get("/admin/orders", { params: { status: statusFilter, q } })
            .then((r) => setOrders(r.data))
            .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const openDetail = (o) => {
        setDetail(o);
        setStatusForm({ status: o.status, courier_name: o.courier?.name || "", courier_tracking_url: o.courier?.tracking_url || "", courier_notes: o.courier?.notes || "" });
        setRefundAmount("");
    };

    const saveStatus = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/orders/${detail.id}/status`, statusForm);
            setDetail(data);
            toast.success(`Status updated — customer emailed`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const quickStatus = async (status, label) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/orders/${detail.id}/status`, { ...statusForm, status });
            setDetail(data);
            setStatusForm((f) => ({ ...f, status }));
            toast.success(`${label} — customer notified by email`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const refund = async (full) => {
        if (!window.confirm(full ? `Refund the full ${money(detail.total)}?` : `Refund ${money(refundAmount)}?`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/orders/${detail.id}/refund`, full ? {} : { amount: Number(refundAmount) });
            setDetail(data);
            toast.success("Refund issued via Stripe");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Refund failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-orders">
            <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                    <Input placeholder="Search order no, name, email" value={q} onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()} className="pl-9 max-w-xs bg-white" data-testid="admin-orders-search" />
                </div>
                <Button variant="outline" onClick={load} className="rounded-full" data-testid="admin-orders-search-btn">Search</Button>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-56 bg-white" data-testid="admin-orders-status-filter">
                        <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <span className="text-sm text-slate-500 self-center ml-auto" data-testid="admin-orders-count">{orders.length} orders</span>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-orders-list">
                {orders.length === 0 && <p className="p-6 text-sm text-slate-400">No orders found.</p>}
                {orders.map((o) => (
                    <button key={o.id} type="button" onClick={() => openDetail(o)} data-testid={`admin-order-row-${o.order_number}`}
                        className="w-full text-left flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors flex-wrap">
                        <div className="min-w-28">
                            <p className="font-heading font-extrabold text-[#0033A0]">{o.order_number}</p>
                            <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("en-GB")}</p>
                        </div>
                        <div className="flex-1 min-w-40">
                            <p className="font-bold text-slate-900 text-sm">{o.customer.name}</p>
                            <p className="text-xs text-slate-500">{o.items.length} item{o.items.length > 1 ? "s" : ""} · {{ collection: "click & collect", local_delivery: "local delivery", courier_delivery: "courier", delivery: "delivery" }[o.fulfilment] || o.fulfilment}</p>
                        </div>
                        <p className="font-heading font-extrabold">{money(o.total)}</p>
                        <StatusPill value={o.status} />
                        <StatusPill value={o.payment_status} />
                    </button>
                ))}
            </div>

            <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-heading flex items-center gap-3 flex-wrap">
                                    {detail.order_number}
                                    <StatusPill value={detail.status} />
                                    <StatusPill value={detail.payment_status} />
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-5 py-2 text-sm">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <p className="font-bold text-slate-900 mb-1">Customer</p>
                                        <p>{detail.customer.name}</p>
                                        <p className="text-slate-500">{detail.customer.email}</p>
                                        {detail.customer.phone && <p className="text-slate-500">{detail.customer.phone}</p>}
                                        {detail.user_id ? <p className="text-xs text-[#0033A0] font-bold mt-1">Registered customer</p> : <p className="text-xs text-slate-400 mt-1">Guest checkout</p>}
                                    </div>
                                    <div>
                                        <p className="font-bold text-slate-900 mb-1">{detail.fulfilment === "collection" ? "Click & Collect" : "Delivery address"}</p>
                                        {detail.address ? (
                                            <p className="text-slate-600">{detail.address.line1}{detail.address.line2 ? `, ${detail.address.line2}` : ""}, {detail.address.city}, {detail.address.postcode}</p>
                                        ) : (
                                            <p className="text-slate-500">Customer collects from the shop</p>
                                        )}
                                        {detail.pickup_time && (
                                            <p className="mt-1 text-sm font-bold text-[#0033A0]" data-testid="order-pickup-time">
                                                Collection: {new Date(detail.pickup_time).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                {detail.perishable_hold && <span className="block text-xs text-amber-700 font-normal">Perishable — hold {detail.perishable_hold_hours}h past collection time</span>}
                                            </p>
                                        )}
                                        <p className="mt-1 text-xs text-slate-500" data-testid="order-payment-method">
                                            Payment: {detail.payment_method === "shop" ? "pay at the shop" : "online (Stripe)"}
                                        </p>
                                        {detail.notes && <p className="mt-2 text-slate-500 italic">"{detail.notes}"</p>}
                                    </div>
                                </div>

                                <div>
                                    <p className="font-bold text-slate-900 mb-2">Items</p>
                                    <ul className="divide-y divide-slate-100">
                                        {detail.items.map((i, idx) => (
                                            <li key={idx} className="py-1.5 flex justify-between">
                                                <span>{i.sold_by_weight ? `${parseFloat(Number(i.qty).toFixed(2))} kg` : i.qty} × {i.name}{i.variation ? ` (${i.variation})` : ""}</span>
                                                <span className="font-bold">{money(i.line_total)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <dl className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                                        <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd>{money(detail.subtotal)}</dd></div>
                                        {detail.discount_amount > 0 && <div className="flex justify-between text-green-700"><dt>Discount ({detail.discount_code})</dt><dd>-{money(detail.discount_amount)}</dd></div>}
                                        <div className="flex justify-between"><dt className="text-slate-500">Delivery</dt><dd>{money(detail.delivery_fee)}</dd></div>
                                        <div className="flex justify-between font-extrabold"><dt>Total</dt><dd>{money(detail.total)}</dd></div>
                                        {detail.refunded_amount > 0 && <div className="flex justify-between text-red-600"><dt>Refunded</dt><dd>-{money(detail.refunded_amount)}</dd></div>}
                                    </dl>
                                </div>

                                <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid="order-status-panel">
                                    {detail.fulfilment === "collection" && (
                                        <div className="flex flex-wrap gap-3" data-testid="collection-actions">
                                            <Button
                                                onClick={() => quickStatus("Ready for Collection", "Marked ready to collect")}
                                                disabled={busy || detail.status === "Ready for Collection" || detail.status === "Collected"}
                                                className="rounded-full bg-[#40E0D0] text-[#0033A0] hover:bg-[#2fd4c4] font-heading font-bold"
                                                data-testid="mark-ready-collect"
                                            >
                                                ✓ Tick as Ready to Collect
                                            </Button>
                                            <Button
                                                onClick={() => quickStatus("Collected", "Marked as collected")}
                                                disabled={busy || detail.status === "Collected"}
                                                variant="outline"
                                                className="rounded-full border-[#0033A0] text-[#0033A0]"
                                                data-testid="mark-collected"
                                            >
                                                Mark as Collected
                                            </Button>
                                        </div>
                                    )}
                                    <p className="font-bold text-slate-900">Update status</p>
                                    <Select value={statusForm.status} onValueChange={(v) => setStatusForm({ ...statusForm, status: v })}>
                                        <SelectTrigger className="bg-white" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUSES.map((s) => (
                                                <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <div>
                                            <Label className="text-xs">Courier name</Label>
                                            <Input value={statusForm.courier_name} onChange={(e) => setStatusForm({ ...statusForm, courier_name: e.target.value })}
                                                placeholder="e.g. DPD" className="bg-white" data-testid="courier-name" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Courier tracking URL</Label>
                                            <Input value={statusForm.courier_tracking_url} onChange={(e) => setStatusForm({ ...statusForm, courier_tracking_url: e.target.value })}
                                                placeholder="https://…" className="bg-white" data-testid="courier-url" />
                                        </div>
                                        <div>
                                            <Label className="text-xs">Delivery notes</Label>
                                            <Input value={statusForm.courier_notes} onChange={(e) => setStatusForm({ ...statusForm, courier_notes: e.target.value })}
                                                placeholder="Optional" className="bg-white" data-testid="courier-notes" />
                                        </div>
                                    </div>
                                    <Button onClick={saveStatus} disabled={busy} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="order-status-save">
                                        {busy ? "Saving…" : "Save & Email Customer"}
                                    </Button>
                                </div>

                                {detail.payment_status === "paid" && (
                                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3" data-testid="refund-panel">
                                        <p className="font-bold text-red-800">Refunds (via Stripe)</p>
                                        <div className="flex flex-wrap gap-3 items-center">
                                            <Input type="number" min="0.01" step="0.01" max={detail.total - (detail.refunded_amount || 0)} value={refundAmount}
                                                onChange={(e) => setRefundAmount(e.target.value)} placeholder="Partial amount £"
                                                className="w-44 bg-white" data-testid="refund-amount" />
                                            <Button variant="outline" onClick={() => refund(false)} disabled={busy || !refundAmount}
                                                className="rounded-full border-red-300 text-red-700 hover:bg-red-100" data-testid="refund-partial">
                                                Refund Partial
                                            </Button>
                                            <Button variant="outline" onClick={() => refund(true)} disabled={busy}
                                                className="rounded-full border-red-300 text-red-700 hover:bg-red-100" data-testid="refund-full">
                                                Refund Full {money(detail.total - (detail.refunded_amount || 0))}
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <p className="font-bold text-slate-900 mb-2">Timeline</p>
                                    <ul className="space-y-1 text-xs text-slate-500" data-testid="order-timeline">
                                        {(detail.timeline || []).map((t, idx) => (
                                            <li key={idx}>
                                                <span className="font-bold text-slate-700">{t.status}</span> — {new Date(t.at).toLocaleString("en-GB")} · {t.by}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
